import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  ICreatePermission,
  IPermission,
  IPermissionResponse,
  IUpdatePermission,
} from "../models/Permission";
import { PermissionService } from "../services/permissionService";

export function useGetAllPermissions(): UseQueryResult<IPermission[]> {
  return useQuery<IPermission[]>({
    queryKey: ["permissions"],
    queryFn: async () => PermissionService.getAllPermissions(),
  });
}
export function useCreatePermission(): UseMutationResult<
  IPermissionResponse,
  Error,
  ICreatePermission,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (permission: ICreatePermission) =>
      PermissionService.createPermission(permission),
    onSuccess: (responseData: IPermissionResponse) => {
      queryClient.setQueryData<IPermission[]>(["permissions"], (oldData) => {
        if (!oldData) return oldData;
        return oldData.map((group) =>
          group.key === responseData.key
            ? {
                ...group,
                children: [...group.children, responseData.children],
              }
            : group
        );
      });
    },
  });
}
export function useUpdatePermission(): UseMutationResult<
  IPermissionResponse,
  Error,
  IUpdatePermission,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (permission: IUpdatePermission) =>
      PermissionService.updatePermission(permission),
    onSuccess: (responseData: IPermissionResponse) => {
      queryClient.setQueryData<IPermission[]>(["permissions"], (oldData) => {
        if (!oldData) return oldData;
        return oldData.map((group) =>
          group.key === responseData.key
            ? {
                ...group,
                children: group.children.map((child) =>
                  child.key === responseData.children.key
                    ? responseData.children
                    : child
                ),
              }
            : group
        );
      });
    },
  });
}
export function useDeletePermission(): UseMutationResult<
  void,
  Error,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (permissionId: number) =>
      PermissionService.deletePermission(permissionId),
    onSuccess: (_, permissionId) => {
      queryClient.setQueryData<IPermission[]>(["permissions"], (oldData) => {
        if (!oldData) return oldData;
        return oldData.map((group) => ({
          ...group,
          children: group.children.filter(
            (child) => child.key !== permissionId.toString()
          ),
        }));
      });
    },
  });
}
